import type { TranscriptEntry } from "../types.ts";
import {
  buildConversationSegments,
  countTranscriptWords,
  mergeConversationSegments,
  speakerIdentity,
  type ConversationSegment,
} from "./conversationSegments.ts";

export type AnswerBlock = ConversationSegment;

export interface GeneratedCheckpointMarker {
  answerBlockStartTs: number;
  checkpointThroughTs: number;
}

export interface EligibleCheckpoint {
  kind: "initial" | "mid" | "handoff";
  block: AnswerBlock;
  checkpointThroughTs: number;
  newDurationMs: number;
  newWordCount: number;
}

export interface ChronologicalEligibleCheckpoint {
  checkpoint: EligibleCheckpoint;
  transcript: TranscriptEntry[];
  now: number;
}

export function buildAnswerBlocks(
  transcript: TranscriptEntry[],
  completionPauseMs: number,
  now: number,
): AnswerBlock[] {
  return buildConversationSegments(transcript, completionPauseMs, now);
}

export function renderAnswerBlockTranscript(block: Pick<AnswerBlock, "entries">): string[] {
  const turns = collapseSpeakerTurns(block.entries);
  const distinctSpeakers = new Set(turns.map((turn) => turn.speakerIdentity));

  if (distinctSpeakers.size <= 1) {
    const text = turns.map((turn) => turn.text).join(" ").trim();
    return text ? [text] : [];
  }

  return turns.map((turn) => `[${turn.speaker}] ${turn.text}`);
}

export function findEligibleCheckpoint(input: {
  transcript: TranscriptEntry[];
  markers: GeneratedCheckpointMarker[];
  thresholdSeconds: number;
  thresholdWords: number;
  completionPauseMs: number;
  now: number;
}): EligibleCheckpoint | null {
  const blocks = buildAnswerBlocks(input.transcript, input.completionPauseMs, input.now);

  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const checkpoint = findEligibleCheckpointForBlock(blocks[i], input);
    if (checkpoint) return checkpoint;

    const aggregateCheckpoint = findEligibleCheckpointForAggregatedWindow(blocks, i, input);
    if (aggregateCheckpoint) return aggregateCheckpoint;
  }

  return null;
}

export function findEligibleCheckpointsChronologically(input: {
  transcript: TranscriptEntry[];
  markers: GeneratedCheckpointMarker[];
  thresholdSeconds: number;
  thresholdWords: number;
  completionPauseMs: number;
  now: number;
  maxCheckpoints?: number;
}): ChronologicalEligibleCheckpoint[] {
  const finals = input.transcript
    .filter((entry) => entry.final && entry.text.trim())
    .sort((a, b) => a.ts - b.ts);
  if (finals.length === 0) return [];

  const maxCheckpoints = normalizeMaxCheckpoints(input.maxCheckpoints, finals.length + 1);
  const markers = [...input.markers];
  const attempts = new Set(markers.map(checkpointKey));
  let coveredThroughTs = markers.reduce(
    (latest, marker) => Math.max(latest, marker.checkpointThroughTs),
    -Infinity,
  );
  const checkpoints: ChronologicalEligibleCheckpoint[] = [];

  function addCheckpoint(
    checkpoint: EligibleCheckpoint | null,
    transcript: TranscriptEntry[],
    now: number,
  ) {
    if (!checkpoint || checkpoints.length >= maxCheckpoints) return;
    if (checkpoint.checkpointThroughTs <= coveredThroughTs) return;
    const marker = {
      answerBlockStartTs: checkpoint.block.startTs,
      checkpointThroughTs: checkpoint.checkpointThroughTs,
    };
    const key = checkpointKey(marker);
    if (attempts.has(key)) return;
    attempts.add(key);
    markers.push(marker);
    coveredThroughTs = marker.checkpointThroughTs;
    checkpoints.push({ checkpoint, transcript, now });
  }

  for (let end = 0; end < finals.length && checkpoints.length < maxCheckpoints; end += 1) {
    const transcript = finals.slice(0, end + 1);
    const now = transcript.at(-1)!.ts + 1_000;
    addCheckpoint(
      findEligibleCheckpoint({
        transcript,
        markers,
        thresholdSeconds: input.thresholdSeconds,
        thresholdWords: input.thresholdWords,
        completionPauseMs: input.completionPauseMs,
        now,
      }),
      transcript,
      now,
    );
  }

  if (checkpoints.length < maxCheckpoints) {
    addCheckpoint(
      findEligibleCheckpoint({
        transcript: finals,
        markers,
        thresholdSeconds: input.thresholdSeconds,
        thresholdWords: input.thresholdWords,
        completionPauseMs: input.completionPauseMs,
        now: input.now,
      }),
      finals,
      input.now,
    );
  }

  return checkpoints;
}

function findEligibleCheckpointForBlock(
  block: AnswerBlock,
  input: {
    markers: GeneratedCheckpointMarker[];
    thresholdSeconds: number;
    thresholdWords: number;
  },
): EligibleCheckpoint | null {
  const thresholdMs = input.thresholdSeconds * 1000;
  const relevantMarkers = input.markers
    .filter((marker) => marker.answerBlockStartTs === block.startTs)
    .sort((a, b) => a.checkpointThroughTs - b.checkpointThroughTs);
  const lastMarker = relevantMarkers.at(-1) ?? null;

  if (!lastMarker) {
    if (block.durationMs < thresholdMs || block.wordCount < input.thresholdWords) {
      return null;
    }

    return {
      kind: block.isClosed ? "handoff" : "initial",
      block,
      checkpointThroughTs: block.endTs,
      newDurationMs: block.durationMs,
      newWordCount: block.wordCount,
    };
  }

  const afterMarkerEntries = block.entries.filter((entry) => entry.ts > lastMarker.checkpointThroughTs);
  const newDurationMs = block.endTs - lastMarker.checkpointThroughTs;
  const newWordCount = countWords(afterMarkerEntries);

  if (newDurationMs < thresholdMs || newWordCount < input.thresholdWords) {
    return null;
  }

  return {
    kind: block.isClosed ? "handoff" : "mid",
    block,
    checkpointThroughTs: block.endTs,
    newDurationMs,
    newWordCount,
  };
}

function findEligibleCheckpointForAggregatedWindow(
  blocks: AnswerBlock[],
  endIndex: number,
  input: {
    markers: GeneratedCheckpointMarker[];
    thresholdSeconds: number;
    thresholdWords: number;
  },
): EligibleCheckpoint | null {
  if (endIndex === 0) return null;

  for (let startIndex = endIndex - 1; startIndex >= 0; startIndex -= 1) {
    if (blocks[startIndex].exchangeGroup !== blocks[endIndex].exchangeGroup) break;
    if (findEligibleCheckpointForBlock(blocks[startIndex], input)) break;
    const window = mergeConversationSegments(blocks.slice(startIndex, endIndex + 1));
    const checkpoint = findEligibleCheckpointForWindow(window, input);
    if (checkpoint) return checkpoint;
  }

  return null;
}

function findEligibleCheckpointForWindow(
  window: AnswerBlock,
  input: {
    markers: GeneratedCheckpointMarker[];
    thresholdSeconds: number;
    thresholdWords: number;
  },
): EligibleCheckpoint | null {
  const thresholdMs = input.thresholdSeconds * 1000;
  const relevantMarkers = input.markers
    .filter((marker) => marker.checkpointThroughTs >= window.startTs && marker.checkpointThroughTs <= window.endTs)
    .sort((a, b) => a.checkpointThroughTs - b.checkpointThroughTs);
  const lastMarker = relevantMarkers.at(-1) ?? null;

  if (!lastMarker) {
    if (window.durationMs < thresholdMs || window.wordCount < input.thresholdWords) {
      return null;
    }

    return {
      kind: window.isClosed ? "handoff" : "initial",
      block: window,
      checkpointThroughTs: window.endTs,
      newDurationMs: window.durationMs,
      newWordCount: window.wordCount,
    };
  }

  const afterMarkerEntries = window.entries.filter((entry) => entry.ts > lastMarker.checkpointThroughTs);
  const newDurationMs = window.endTs - lastMarker.checkpointThroughTs;
  const newWordCount = countWords(afterMarkerEntries);

  if (newDurationMs < thresholdMs || newWordCount < input.thresholdWords) {
    return null;
  }

  return {
    kind: window.isClosed ? "handoff" : "mid",
    block: window,
    checkpointThroughTs: window.endTs,
    newDurationMs,
    newWordCount,
  };
}

export function collapseSpeakerTurns(
  entries: Array<Pick<TranscriptEntry, "speaker" | "speakerKey" | "text">>,
): Array<{ speaker: string; speakerIdentity: string; text: string }> {
  const turns: Array<{ speaker: string; speakerIdentity: string; text: string }> = [];

  for (const entry of entries) {
    const text = entry.text.trim();
    if (!text) continue;
    const lastTurn = turns.at(-1);
    const identity = speakerIdentity(entry);
    if (lastTurn && lastTurn.speakerIdentity === identity) {
      lastTurn.text = `${lastTurn.text} ${text}`;
      continue;
    }
    turns.push({ speaker: entry.speaker, speakerIdentity: identity, text });
  }

  return turns;
}

function countWords(entries: TranscriptEntry[]): number {
  return countTranscriptWords(entries);
}

function normalizeMaxCheckpoints(value: number | undefined, fullTranscriptLimit: number): number {
  if (value === undefined || !Number.isFinite(value)) return fullTranscriptLimit;
  return Math.max(1, Math.min(fullTranscriptLimit, Math.floor(value)));
}

function checkpointKey(marker: GeneratedCheckpointMarker): string {
  return `${marker.answerBlockStartTs}:${marker.checkpointThroughTs}`;
}
